import React, { useState } from "react"
import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { Group, PlayArrow, SwapHoriz, Stop, BarChart } from "@mui/icons-material"
import { AIExperiment, ExperimentStatus, armForCohort } from "../../../../services/ai-experiment.service"
import { DataTable, DataTableColumn, EmptyState, StatusBadge, TagChip } from "../../../../components/ui"

type Props = {
    experiments: AIExperiment[]
    onManage: (exp: AIExperiment) => void
    onStart: (exp: AIExperiment) => void
    onCrossover: (exp: AIExperiment) => void
    onComplete: (exp: AIExperiment) => void
    onResults: (exp: AIExperiment) => void
    countLabelText?: string
}

const STATUS_LABEL: Record<ExperimentStatus, string> = {
    draft: "Rascunho",
    running: "Em andamento",
    completed: "Concluído",
}

export const AIExperimentTable: React.FC<Props> = ({
    experiments,
    onManage,
    onStart,
    onCrossover,
    onComplete,
    onResults,
    countLabelText,
}) => {
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 25

    const columns: DataTableColumn<AIExperiment>[] = [
        {
            key: "name",
            header: "Nome",
            width: "2fr",
            render: (row) => (
                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "text.primary" }}>
                    {row.name}
                </Typography>
            ),
        },
        {
            key: "providers",
            header: "Provedores A/B",
            width: "1.5fr",
            render: (row) => {
                const armA = armForCohort("A", row.current_round, row.provider_a, row.provider_b)
                const armB = armForCohort("B", row.current_round, row.provider_a, row.provider_b)
                return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <TagChip label={`A → ${armA}`} maxChars={20} />
                        <TagChip label={`B → ${armB}`} maxChars={20} />
                    </Box>
                )
            },
        },
        {
            key: "status",
            header: "Status",
            width: "130px",
            render: (row) => {
                const label = STATUS_LABEL[row.status]
                // "em andamento" não está no mapa interno do StatusBadge — forçar tone
                const tone = row.status === "running" ? "success" as const
                    : row.status === "completed" ? "success" as const
                    : "warning" as const
                return <StatusBadge status={label} tone={tone} />
            },
        },
        {
            key: "round",
            header: "Rodada",
            width: "80px",
            render: (row) => (
                <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                    {row.current_round}
                </Typography>
            ),
        },
        {
            key: "actions",
            header: "Ações",
            width: "160px",
            align: "right",
            render: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.25 }}>
                    <Tooltip title="Participantes / inscrever">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onManage(row) }}
                            sx={{ borderRadius: "9px" }}
                        >
                            <Group sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    {row.status === "draft" && (
                        <Tooltip title="Iniciar experimento">
                            <IconButton
                                size="small"
                                color="success"
                                onClick={(e) => { e.stopPropagation(); onStart(row) }}
                                sx={{ borderRadius: "9px" }}
                            >
                                <PlayArrow sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {row.status === "running" && (
                        <>
                            <Tooltip title="Crossover (trocar braços)">
                                <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={(e) => { e.stopPropagation(); onCrossover(row) }}
                                    sx={{ borderRadius: "9px" }}
                                >
                                    <SwapHoriz sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Concluir experimento">
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => { e.stopPropagation(); onComplete(row) }}
                                    sx={{ borderRadius: "9px" }}
                                >
                                    <Stop sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Resultados">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onResults(row) }}
                            sx={{ borderRadius: "9px" }}
                        >
                            <BarChart sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ]

    return (
        <DataTable<AIExperiment>
            columns={columns}
            rows={experiments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
            rowKey={(row) => String(row.id)}
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={experiments.length}
            onPageChange={setPage}
            countLabel={countLabelText}
            emptyState={
                <EmptyState
                    title="Nenhum experimento cadastrado"
                    description="Crie o primeiro experimento para começar."
                />
            }
            minWidth={700}
        />
    )
}
